import { useTranslate } from "@refinedev/core";
import { SceneEnableSwitch } from "./SceneEnableSwitch";
import { useTemplateData } from "../../hooks/useTemplateData";
import { useTemplateEditorStore } from "../../store/useTemplateEditorStore";
import { getClosingEnabled, getIntroEnabled } from "../../helpers";
import { TemplateType } from "../../types";
import { ENABLE_CLOSING_KEY, ENABLE_INTRO_KEY } from "../../constants";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";

export type IntroClosingEnableSwitchProps = {
  isIntro: boolean;
};

export const IntroClosingEnableSwitch = ({
  isIntro,
}: IntroClosingEnableSwitchProps) => {
  const t = useTranslate();

  const {
    brandKitData: { intro, closing },
    storyboardSections,
    mediaGenerationSettingsData: {
      mediaGenerationSettings,
      setIntroClosingEnabled,
    },
  } = useTemplateData();

  const { currentChapter, setCurrentChapter } = useTemplateEditorStore(
    (state) => ({
      currentChapter: state.currentChapter,
      setCurrentChapter: state.setCurrentChapter,
    })
  );

  const checked = isIntro
    ? getIntroEnabled(mediaGenerationSettings, !!intro)
    : getClosingEnabled(mediaGenerationSettings, !!closing);

  const navigateToChapter = (section: SectionWithSlides | undefined) => {
    if (!section) return;
    const chapterTemplate =
      mediaGenerationSettings?.chapters.find(
        (chapter) => chapter.chapter_id === section.id
      )?.chapter_template_id ?? undefined;
    setCurrentChapter({
      templateId: chapterTemplate,
      topic: section.topic,
      sectionId: section.id,
      type: TemplateType.CHAPTER,
    });
  };

  const handleChange = (next: boolean) => {
    const sceneType = isIntro
      ? TemplateType.INTRODUCTION
      : TemplateType.CLOSING;

    if (!next && currentChapter.type === sceneType) {
      navigateToChapter(
        isIntro
          ? storyboardSections[0]
          : storyboardSections[storyboardSections.length - 1]
      );
    }

    setIntroClosingEnabled(
      isIntro ? ENABLE_INTRO_KEY : ENABLE_CLOSING_KEY,
      next
    ).catch((error) =>
      console.error(`Error toggling ${isIntro ? "intro" : "closing"}`, error)
    );
  };

  const action = checked ? "disable" : "enable";
  const scene = isIntro ? "Intro" : "Closing";
  const key = `components.templates.TemplatesBoard.storyboardSlidesThumbnails.${action}${scene}`;

  return (
    <SceneEnableSwitch
      checked={checked}
      onChange={handleChange}
      tooltipTitle={t(key)}
    />
  );
};
